import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	useGetArchivedVideoWizardsQuery,
	useGetCourseVideoWizardQuery,
} from "@/pages/Private/redux/media/videoWizard.api";
import { WizardStepStatus } from "@/pages/Private/redux/media/videoWizard.schema";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";
import { formatUsd } from "@/utils/formatUsd";

interface IntroVideoTabProps {
	courseId?: number;
}

/**
 * The wizard's entry point: what the flow does, roughly what it costs, and the way in.
 *
 * The per-step figures are illustrative — once the wizard is open its own rail carries the
 * real estimate, which comes from the server like every other price. The *state*, though, is
 * real: this used to say "not started yet" whatever had happened, so a scenario with a
 * finished intro video and eight dollars of history looked untouched, and the only way in
 * was a button marked "start". A step already approved is now marked as such, and the button
 * says what it will actually do.
 */
export const IntroVideoTab: FunctionComponent<IntroVideoTabProps> = ({ courseId }) => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`courses.introVideo.${key}`, options ?? {}),
		[t]
	);

	const steps = ["storyboard", "style", "narration", "audio", "sceneVideos", "cutExport"];

	// `POST` on the wizard route is create-or-resume, so the button always lands the author
	// in the open session — but until this query existed the tab could not say which of the
	// two it would be.
	const { data: wizard } = useGetCourseVideoWizardQuery(courseId as number, { skip: !courseId });
	// Sessions the author archived (BCM360-375): read-only, for their date and their cost.
	const { data: archived = [] } = useGetArchivedVideoWizardsQuery(courseId as number, {
		skip: !courseId,
	});
	const dateLocale = i18n.language?.startsWith("de") ? "de-DE" : "en-US";

	const approvedSteps = (wizard?.steps ?? []).filter(
		(step) => step.status === WizardStepStatus.APPROVED
	).length;
	const spentUsd = wizard?.costs?.totalActualUsd ?? 0;
	const isStarted =
		!!wizard && (approvedSteps > 0 || spentUsd > 0 || (wizard.activeJobs?.length ?? 0) > 0);
	const isFinished = !!wizard?.introCourseFileUrl;

	return (
		<div className="max-w-[760px] space-y-4 rounded-card border border-border-soft bg-white p-5">
			<div className="flex items-start justify-between gap-4">
				<div>
					<h3 className="text-base font-bold text-ink">{ts("title")}</h3>
					<p className="mt-1 max-w-[540px] text-xs text-subtle">{ts("description")}</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						isFinished
							? "bg-success-light text-text"
							: isStarted
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{isFinished
						? ts("finished")
						: isStarted
							? // "Läuft" only while a job is in flight; a session left open is merely
								// started — "Läuft — Schritt 5" read as work going on for weeks (BCM360-373).
								t(
									(wizard?.activeJobs?.length ?? 0) > 0
										? "courses.introVideo.inProgress"
										: "courses.introVideo.started",
									{ step: wizard?.currentStep ?? 1 }
								)
							: ts("notStarted")}
				</span>
			</div>

			<ol className="overflow-hidden rounded-card border border-border">
				{steps.map((step, index) => (
					<li
						key={step}
						className="flex items-center gap-3 border-b border-border-soft px-4 py-2.5 last:border-b-0"
					>
						<span
							className={classNames(
								"flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
								wizard?.steps?.find((entry) => entry.step === index + 1)?.status ===
									WizardStepStatus.APPROVED
									? "bg-success-light text-text"
									: "bg-surface text-subtle"
							)}
						>
							{wizard?.steps?.find((entry) => entry.step === index + 1)?.status ===
							WizardStepStatus.APPROVED
								? "✓"
								: index + 1}
						</span>
						<div className="flex-1">
							<b className="text-[13px] text-ink">{ts(`steps.${step}`)}</b>
							<div className="text-xs text-subtle">{ts(`steps.${step}Hint`)}</div>
						</div>
						{/* What this step actually cost, once it has run — the illustrative
						    estimate is only interesting until there is a real figure. */}
						{(() => {
							const state = wizard?.steps?.find((entry) => entry.step === index + 1);
							const actual = state?.actualCostUsd ?? 0;

							return actual > 0 ? (
								<span className="text-xs font-bold text-ink">{formatUsd(actual)}</span>
							) : (
								<span className="text-xs font-bold text-subtle">{ts(`steps.${step}Estimate`)}</span>
							);
						})()}
					</li>
				))}
			</ol>

			<div className="flex items-center justify-between rounded-card bg-surface px-4 py-3">
				<span className="text-xs font-semibold text-subtle">
					{isStarted ? ts("spent") : ts("forecast")}
				</span>
				<span className="text-sm font-bold text-ink">
					{isStarted ? formatUsd(spentUsd) : ts("forecastValue")}
				</span>
			</div>

			<Button
				className="w-full"
				color={ButtonColor.ACTION}
				disabled={!courseId}
				size={ButtonSize.M}
				title={isFinished ? ts("openFinished") : isStarted ? ts("continue") : ts("start")}
				type="button"
				onClick={() => courseId && navigate(`${ROUTE_CONFIG.COURSES}/edit/${courseId}/intro-video`)}
			/>

			{!!archived.length && (
				<section className="space-y-2 border-t border-border-soft pt-4">
					<h4 className="text-xs font-bold uppercase text-subtle">{ts("archivedHeading")}</h4>
					<ul
						className="divide-y divide-border-soft rounded-card border border-border-soft"
						role="list"
					>
						{archived.map((session) => (
							<li key={session.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
								<span className="text-xs text-text">
									{ts("archivedRow", {
										date: session.archivedAt
											? new Date(session.archivedAt).toLocaleDateString(dateLocale)
											: "—",
										step: session.unlockedStep,
										// React escapes the text already; i18next's own escaping would
										// print an English date as "9&#x2F;30&#x2F;2026".
										interpolation: { escapeValue: false },
									})}
								</span>
								<span className="text-xs font-bold text-ink">
									{formatUsd(session.actualCostUsd)}
								</span>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	);
};
